import { useState, useEffect } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Phone, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'Ausstehend', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  reached: { label: 'Erreicht', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  not_reached: { label: 'Nicht erreicht', cls: 'bg-red-50 text-red-700 border-red-200' },
  mailbox: { label: 'Mailbox', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  callback: { label: 'Rückruf', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  no_interest: { label: 'Kein Interesse', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function CallHistorySheet({ lead, onClose }) {
  const { token } = useAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (lead) {
      setLoading(true);
      axios.get(`${API}/calls/lead/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setCalls(res.data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [lead, token]);

  return (
    <Sheet open={!!lead} onOpenChange={onClose}>
      <SheetContent className="rounded-sm w-full sm:max-w-md" data-testid="call-history-sheet">
        <SheetHeader>
          <SheetTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
            Anrufverlauf
          </SheetTitle>
          <SheetDescription>
            {lead?.name} &middot; <span className="font-mono">{lead?.phone}</span>
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-140px)] mt-4 pr-2">
          {loading ? (
            <p className="text-slate-400 text-center py-8 text-sm">Laden...</p>
          ) : calls.length === 0 ? (
            <div className="text-center py-12" data-testid="no-calls-message">
              <Phone className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Keine Anrufe vorhanden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map(call => {
                const statusCfg = STATUS_MAP[call.status] || { label: call.status, cls: 'bg-slate-100 text-slate-500' };
                return (
                  <div
                    key={call.id}
                    className="border border-slate-200 rounded-sm p-4"
                    data-testid={`call-history-item-${call.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-slate-700">{call.phone_number}</span>
                      <Badge variant="outline" className={`rounded-sm text-xs ${statusCfg.cls}`}>
                        {statusCfg.label}
                      </Badge>
                    </div>
                    {call.notes && (
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{call.notes}</p>
                    )}
                    <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{formatTime(call.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
